import { useState, useEffect, useCallback } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { NotificationBell } from '@/components/NotificationBell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, CheckCircle2, Clock, AlertCircle, Loader2, Download } from 'lucide-react';

export const UserPayments = () => {
  const [records, setRecords] = useState([]);
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState('all');
  const [payingId, setPayingId] = useState(null);
  const [pollingSessionId, setPollingSessionId] = useState(null);
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchData = useCallback(async () => {
    try {
      const [recordsRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/payment-records`, { withCredentials: true }),
        axios.get(`${API_URL}/api/payments`, { withCredentials: true }),
      ]);
      setRecords(recordsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    }
  }, [API_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check for returning from PayTR
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      setPollingSessionId(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Poll payment status
  useEffect(() => {
    if (!pollingSessionId) return;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/checkout/status/${pollingSessionId}`, { withCredentials: true });
        if (data.payment_status === 'paid') {
          toast.success('Ödeme başarılı! Teşekkürler.');
          setPollingSessionId(null);
          fetchData();
          return;
        }
        if (data.status === 'expired') {
          toast.error('Ödeme süresi doldu. Lütfen tekrar deneyin.');
          setPollingSessionId(null);
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Ödeme durumu kontrol ediliyor, lütfen sayfayı yenileyin.');
          setPollingSessionId(null);
        }
      } catch (e) {
        setPollingSessionId(null);
      }
    };

    poll();
  }, [pollingSessionId, API_URL, fetchData]);

  const handleOnlinePayment = async (recordId) => {
    setPayingId(recordId);
    try {
      const { data } = await axios.post(`${API_URL}/api/checkout/create`, {
        record_id: recordId,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (data.url) {
        window.location.href = `/checkout?title=${encodeURIComponent('Ödeme')}&return=${encodeURIComponent('/payments')}&url=${encodeURIComponent(data.url)}`;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ödeme başlatılamadı');
      setPayingId(null);
    }
  };

  const handleDownloadReceipt = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/checkout/receipt/${sessionId}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `odeme_makbuzu_${sessionId.slice(-12)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Makbuz indiriliyor');
    } catch (err) {
      toast.error('Makbuz indirilemedi');
    }
  };

  const getAllItems = () => {
    return records.map((record) => {
      const payment = payments.find((p) => p.id === record.payment_id);
      if (!payment) return null;
      return {
        id: record.id,
        title: payment.title,
        description: payment.description || '-',
        amount: record.amount,
        dueDate: new Date(payment.due_date),
        status: record.status,
        paidVia: record.paid_via,
        sessionId: record.session_id,
        recordId: record.id,
      };
    }).filter(Boolean).sort((a, b) => a.dueDate - b.dueDate);
  };

  const allItems = getAllItems();
  const filteredItems = allItems.filter((item) => {
    if (filter === 'all') return true;
    return item.status === filter;
  });

  const totalPending = allItems.filter(i => i.status === 'pending').reduce((s, i) => s + i.amount, 0);
  const totalOverdue = allItems.filter(i => i.status === 'overdue').reduce((s, i) => s + i.amount, 0);
  const totalPaid = allItems.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
  const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      paid: { label: 'Ödendi', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      overdue: { label: 'Gecikmiş', color: 'bg-red-100 text-red-800', icon: AlertCircle },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${c.color}`}>
        <Icon className="w-3 h-3" /> {c.label}
      </span>
    );
  };

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" data-testid="user-payments-title">
              Ödemelerim
            </h1>
            <p className="text-sm text-[#595959] mt-1">Ödeme listesi ve online ödeme</p>
          </div>
          <NotificationBell />
        </div>

        {/* Polling indicator */}
        {pollingSessionId && (
          <Card className="border-2 border-blue-200 bg-blue-50 mb-4">
            <CardContent className="p-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-sm text-blue-700 font-medium">Ödeme durumu kontrol ediliyor...</p>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="border border-[#E5E5E5]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-[#595959]">Bekleyen</p>
                <Clock className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-lg sm:text-xl font-bold text-[#1A1A2E] mt-1" data-testid="pending-total">{fmt(totalPending)} TL</p>
            </CardContent>
          </Card>
          <Card className="border border-[#E5E5E5]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-[#595959]">Gecikmiş</p>
                <AlertCircle className="w-4 h-4 text-red-500" />
              </div>
              <p className="text-lg sm:text-xl font-bold text-red-600 mt-1" data-testid="overdue-total">{fmt(totalOverdue)} TL</p>
            </CardContent>
          </Card>
          <Card className="border border-[#E5E5E5]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-[#595959]">Ödenen</p>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              </div>
              <p className="text-lg sm:text-xl font-bold text-green-700 mt-1" data-testid="paid-total">{fmt(totalPaid)} TL</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="mb-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="payment-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="pending">Bekleyen</SelectItem>
              <SelectItem value="paid">Ödenen</SelectItem>
              <SelectItem value="overdue">Gecikmiş</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block">
          <Card className="border border-[#E5E5E5]" data-testid="user-payments-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ödeme</TableHead>
                  <TableHead>Açıklama</TableHead>
                  <TableHead className="text-right">Tutar</TableHead>
                  <TableHead>Son Tarih</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-[#595959]">
                      Ödeme bulunmuyor
                    </TableCell>
                  </TableRow>
                ) : filteredItems.map((item) => (
                  <TableRow key={item.id} data-testid={`payment-item-${item.id}`}>
                    <TableCell className="font-medium">{item.title}</TableCell>
                    <TableCell className="text-sm text-[#595959]">{item.description}</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(item.amount)} TL</TableCell>
                    <TableCell className={`text-sm ${item.status === 'overdue' ? 'text-red-600 font-medium' : ''}`}>
                      {item.dueDate.toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell>{getStatusBadge(item.status)}</TableCell>
                    <TableCell className="text-right">
                      {item.status !== 'paid' && (
                        <Button
                          size="sm"
                          onClick={() => handleOnlinePayment(item.recordId)}
                          disabled={payingId === item.recordId}
                          className="bg-[#2563EB] hover:bg-[#1E4ED8] gap-1.5"
                          data-testid={`pay-online-${item.id}`}
                        >
                          {payingId === item.recordId ? (
                            <><Loader2 className="w-3 h-3 animate-spin" /> İşleniyor</>
                          ) : (
                            <><CreditCard className="w-3 h-3" /> Online Öde</>
                          )}
                        </Button>
                      )}
                      {item.status === 'paid' && (
                        <div className="flex items-center gap-2 justify-end">
                          {item.paidVia === 'online' && (
                            <span className="text-xs text-green-600 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Online</span>
                          )}
                          {item.sessionId && (
                            <Button variant="outline" size="sm" className="gap-1 text-[#2563EB]" onClick={() => handleDownloadReceipt(item.sessionId)} data-testid={`receipt-${item.id}`}>
                              <Download className="w-3 h-3" /> Dekont
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {filteredItems.length === 0 ? (
            <Card className="border border-[#E5E5E5]">
              <CardContent className="py-8 text-center text-[#595959]">Ödeme bulunmuyor</CardContent>
            </Card>
          ) : filteredItems.map((item) => (
            <Card key={item.id} className="border border-[#E5E5E5]" data-testid={`payment-card-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1A1A2E]">{item.title}</p>
                    <p className="text-xs text-[#595959]">{item.description}</p>
                  </div>
                  <p className="font-bold text-[#1A1A2E] ml-2 whitespace-nowrap">{fmt(item.amount)} TL</p>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  {getStatusBadge(item.status)}
                  <span className={`text-xs ${item.status === 'overdue' ? 'text-red-600' : 'text-[#595959]'}`}>
                    Son: {item.dueDate.toLocaleDateString('tr-TR')}
                  </span>
                </div>
                {item.status !== 'paid' && (
                  <Button
                    className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] gap-2"
                    onClick={() => handleOnlinePayment(item.recordId)}
                    disabled={payingId === item.recordId}
                    data-testid={`pay-mobile-${item.id}`}
                  >
                    {payingId === item.recordId ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> İşleniyor...</>
                    ) : (
                      <><CreditCard className="w-4 h-4" /> Online Öde</>
                    )}
                  </Button>
                )}
                {item.status === 'paid' && (
                  <div className="flex items-center gap-2">
                    {item.paidVia === 'online' && (
                      <span className="text-xs text-green-600 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Online ödendi</span>
                    )}
                    {item.sessionId && (
                      <Button variant="outline" size="sm" className="gap-1 text-[#2563EB] ml-auto" onClick={() => handleDownloadReceipt(item.sessionId)} data-testid={`receipt-mobile-${item.id}`}>
                        <Download className="w-3 h-3" /> Dekont İndir
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </PageLayout>
  );
};
